import { useCallback } from 'react';

import { useClient } from 'urql';

import { type Order } from '@sg/graphql-schema';

import { BagCartDocument, type BagCartQuery } from '../../GraphQL/BagCart.generated';
import {
  BagTimesPollingDocument,
  type BagTimesPollingQuery,
} from '../../GraphQL/BagTimesPolling.generated';
import { prepareCart } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching the customer's cart data.
 */
export const useCart = () => {
  const client = useClient();

  // ─── Queries ──────────────────────────────────────────────────────────────

  const cartQuery = useCallback(async () => {
    const queryCart = client.query<BagCartQuery>;

    return queryCart(BagCartDocument, {}, { requestPolicy: 'network-only' }).toPromise();
  }, [client.query]);

  const wantedTimesQuery = useCallback(async () => {
    const queryWantedTimes = client.query<BagTimesPollingQuery>;

    const response = await queryWantedTimes(
      BagTimesPollingDocument,
      {},
      { requestPolicy: 'network-only' },
    ).toPromise();

    const availableWantedTimes = response.data?.cart?.availableWantedTimes ?? [];

    return { availableWantedTimes };
  }, [client.query]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const fetchCart = useCallback(async () => {
    const [cartResponse, wantedTimesResponse] = await Promise.all([
      cartQuery(),
      wantedTimesQuery(),
    ]);

    const cart = cartResponse.data?.cart ?? {};
    const availableWantedTimes = wantedTimesResponse.availableWantedTimes;

    return prepareCart({
      ...(cart as Partial<Order>),
      availableWantedTimes,
    });
  }, [cartQuery, wantedTimesQuery]);

  // ──────────────────────────────────────────────────────────────────────────

  return { fetchCart };
};
